import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { AppContext } from '../../context/AppContext';
import Avatar from '../../components/Avatar';
import CKMailEditor from '../../components/CKMailEditor';
import TipTapEditor from '../../components/TipTapEditor';
import { sanitizeHtml, stripHtmlTags, formatMailDateTime, isInternalRecipient } from '../../utils/sanitize';
import { 
  Search, 
  Mail, 
  Send, 
  Paperclip, 
  Download, 
  X, 
  ArrowLeft, 
  Star, 
  Trash2, 
  Ban, 
  MailOpen, 
  Plus, 
  CheckCheck,
  Eye,
  Camera,
  UploadCloud,
  ChevronRight,
  CheckCircle2,
  CornerUpLeft,
  ReplyAll,
  CornerUpRight,
  Sparkles,
  MessageSquare,
  RefreshCw,
  Loader2
} from 'lucide-react';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';
import { generateEmailSummary } from '../../utils/aiSummarizer';

const MobileMailTab = ({ initialCompose, onClearInitialCompose, initialUnreadOnly, onClearInitialUnreadOnly }) => {
  const { 
    emails, 
    sendEmail, 
    currentUser, 
    getUserSignature, 
    users, 
    starredEmailIds, 
    toggleStarEmail, 
    drafts, 
    saveDraft, 
    deleteDraft, 
    deleteEmails, 
    markEmailsRead, 
    reportSpamEmails, 
    restoreSpamEmails,
    groupMails,
    customers,
    blockedSenders,
    blockSender,
    sendKakaoBizMessage,
    formatKakaoTemplate,
    kakaoBizConfig,
    fetchRemoteEmails,
    contracts
  } = useContext(AppContext);

  // Check if current user has an approved resignation
  const myApprovedResignation = (contracts || []).find(c => {
    if (!currentUser) return false;
    const myId = currentUser.UserID;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myName = currentUser.Name;
    const isMine = (c.UserID && c.UserID === myId) || 
                   (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || 
                   (c.UserName === myName);
    const isResig = Boolean(c.IsResignation || c.ContractType === 'RESIGNATION' || c.ContractType === 'RESIGNATION_NDA');
    const isApproved = c.Status === 'APPROVED' || c.Status === 'SIGNED' || c.ResignationStatus === 'APPROVED';
    return isMine && isResig && isApproved;
  });

  const resignationDDayInfo = (() => {
    if (!myApprovedResignation) return null;
    const resigDateStr = myApprovedResignation.ResignationDate || myApprovedResignation.ContractEndDate;
    if (!resigDateStr) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(resigDateStr);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let label = '';
    let badgeClass = '';
    let subMessage = '';

    if (diffDays > 0) {
      label = `D-${diffDays}`;
      badgeClass = 'bg-rose-500 text-white';
      subMessage = `퇴사 예정일(${resigDateStr})까지 ${diffDays}일 남았습니다.`;
    } else if (diffDays === 0) {
      label = 'D-Day';
      badgeClass = 'bg-rose-600 text-white animate-pulse';
      subMessage = `오늘(${resigDateStr})이 최종 퇴사일입니다.`;
    } else {
      label = `D+${Math.abs(diffDays)}`;
      badgeClass = 'bg-slate-600 text-white';
      subMessage = `퇴사일(${resigDateStr})이 경과하였습니다.`;
    }

    return {
      resignationDate: resigDateStr,
      diffDays,
      dDayLabel: label,
      badgeClass,
      subMessage,
      contract: myApprovedResignation
    };
  })();

  const [mobileKakaoSuccess, setMobileKakaoSuccess] = useState(false);
  const [mobileKakaoSending, setMobileKakaoSending] = useState(false);

  const [currentFolder, setCurrentFolder] = useState('inbox'); // 'inbox' | 'sent' | 'starred' | 'draft' | 'spam' | 'trash' | customFolderId
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(() => !!initialUnreadOnly);
  const [attachmentOnly, setAttachmentOnly] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Custom Mailboxes, Trash Retention & Sender Routing Rules in Mobile
  const [customFolders, setCustomFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_custom_mailboxes_v1');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  const [trashRetentionDays, setTrashRetentionDays] = useState(() => {
    return Number(localStorage.getItem('clou_trash_retention_days')) || 7;
  });

  const [trashEmailMap, setTrashEmailMap] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_trash_emails_v1');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [customFolderMap, setCustomFolderMap] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_custom_folder_map_v1');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [senderRoutingRules, setSenderRoutingRules] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_sender_routing_rules_v1');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const saveCustomFolders = (folders) => {
    setCustomFolders(folders);
    localStorage.setItem('clou_custom_mailboxes_v1', JSON.stringify(folders));
  };

  const saveTrashEmailMap = (map) => {
    setTrashEmailMap(map);
    localStorage.setItem('clou_trash_emails_v1', JSON.stringify(map));
  };

  const saveCustomFolderMap = (map) => {
    setCustomFolderMap(map);
    localStorage.setItem('clou_custom_folder_map_v1', JSON.stringify(map));
  };

  const saveSenderRoutingRules = (rules) => {
    setSenderRoutingRules(rules);
    localStorage.setItem('clou_sender_routing_rules_v1', JSON.stringify(rules));
  };

  const handleAddCustomFolder = () => {
    const folderName = prompt('추가할 메일함 이름을 입력하세요:');
    if (!folderName || !folderName.trim()) return;
    const newFolder = { id: `folder-${Date.now()}`, name: folderName.trim() };
    saveCustomFolders([...customFolders, newFolder]);
    setCurrentFolder(newFolder.id);
  };

  const handleRenameCustomFolder = (folderId, oldName) => {
    const newName = prompt('수정할 메일함 이름을 입력하세요:', oldName);
    if (!newName || !newName.trim() || newName.trim() === oldName) return;
    saveCustomFolders(customFolders.map(f => f.id === folderId ? { ...f, name: newName.trim() } : f));
  };

  const handleDeleteCustomFolder = (folderId, folderName) => {
    if (confirm(`'${folderName}' 메일함을 삭제하시겠습니까?\n(해당 메일함에 보관되어 있던 메일들은 모두 '받은메일함'으로 원복됩니다)`)) {
      saveCustomFolders(customFolders.filter(f => f.id !== folderId));

      const nextFolderMap = { ...customFolderMap };
      Object.keys(nextFolderMap).forEach(emailId => {
        if (nextFolderMap[emailId] === folderId) {
          delete nextFolderMap[emailId];
        }
      });
      saveCustomFolderMap(nextFolderMap);

      const nextRoutingRules = { ...senderRoutingRules };
      Object.keys(nextRoutingRules).forEach(sender => {
        if (nextRoutingRules[sender] === folderId) {
          delete nextRoutingRules[sender];
        }
      });
      saveSenderRoutingRules(nextRoutingRules);

      if (currentFolder === folderId) setCurrentFolder('inbox');
    }
  };

  const handleMoveToTrash = (emailId) => {
    const nowIso = new Date().toISOString();
    const nextMap = { ...trashEmailMap, [emailId]: nowIso };
    saveTrashEmailMap(nextMap);
    setSelectedEmail(null);
  };

  const handleRestoreFromTrash = (emailId) => {
    const nextMap = { ...trashEmailMap };
    delete nextMap[emailId];
    saveTrashEmailMap(nextMap);
    setSelectedEmail(null);
  };

  const handlePermanentDelete = (emailId) => {
    deleteEmails([emailId]);
    const nextMap = { ...trashEmailMap };
    delete nextMap[emailId];
    saveTrashEmailMap(nextMap);
    setSelectedEmail(null);
  };

  const [moveOptionModal, setMoveOptionModal] = useState({
    isOpen: false,
    emailIds: [],
    targetFolderId: '',
    targetFolderName: '',
    sender: '',
    sameSenderCount: 0
  });

  const promptMoveToCustomFolder = (emailId, targetFolderId) => {
    if (!emailId || !targetFolderId) return;

    const targetFolderObj = customFolders.find(f => f.id === targetFolderId);
    const targetFolderName = targetFolderId === 'inbox' ? '받은메일함' : (targetFolderObj ? targetFolderObj.name : '해당 메일함');

    // Never touch sent emails or trashed emails
    const targetEmail = emails.find(e => e.EmailID === emailId && !isTrashEmail(e) && !isSentEmail(e));
    if (!targetEmail) return;

    const primarySender = (targetEmail.Sender || '').toLowerCase();
    // Only non-trash, NON-SENT received inbox emails
    const sameSenderMails = emails.filter(e => !isTrashEmail(e) && !isSentEmail(e) && isMyInboxEmail(e) && (e.Sender || '').toLowerCase() === primarySender);

    if (sameSenderMails.length > 1) {
      setMoveOptionModal({
        isOpen: true,
        emailIds: [emailId],
        targetFolderId,
        targetFolderName,
        sender: targetEmail.Sender || primarySender,
        sameSenderCount: sameSenderMails.length
      });
    } else {
      executeMoveToCustomFolder([emailId], targetFolderId, false);
    }
  };

  const executeMoveToCustomFolder = (emailIds, targetFolderId, moveAllBySender = false) => {
    const ids = Array.isArray(emailIds) ? emailIds : [emailIds];
    const validMails = emails.filter(e => ids.includes(e.EmailID) && !isTrashEmail(e) && !isSentEmail(e));
    const nextMap = { ...customFolderMap };
    const nextRules = { ...senderRoutingRules };

    if (moveAllBySender && validMails.length > 0) {
      const sender = (validMails[0].Sender || '').toLowerCase();
      // Only non-trash, NON-SENT received inbox emails
      const sameSenderMails = emails.filter(e => !isTrashEmail(e) && !isSentEmail(e) && isMyInboxEmail(e) && (e.Sender || '').toLowerCase() === sender);

      if (targetFolderId === 'inbox') {
        delete nextRules[sender];
        sameSenderMails.forEach(e => {
          delete nextMap[e.EmailID];
        });
      } else {
        nextRules[sender] = targetFolderId;
        sameSenderMails.forEach(e => {
          nextMap[e.EmailID] = targetFolderId;
        });
      }
      saveSenderRoutingRules(nextRules);
    } else {
      ids.forEach(id => {
        if (targetFolderId === 'inbox') {
          delete nextMap[id];
        } else {
          nextMap[id] = targetFolderId;
        }
      });
    }

    saveCustomFolderMap(nextMap);
    setMoveOptionModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleEmptyTrash = () => {
    const trashMails = emails.filter(e => isTrashEmail(e) && (isMyInboxEmail(e) || isSentEmail(e)));
    const trashIds = trashMails.map(e => e.EmailID);
    if (trashIds.length === 0) {
      alert('휴지통이 비어있습니다.');
      return;
    }
    if (confirm(`휴지통의 메일 ${trashIds.length}개를 영구 삭제하시겠습니까? (복구 불가)`)) {
      handlePermanentDelete(trashIds);
    }
  };

  // Auto-purge expired trash emails permanently from AWS DynamoDB & local state
  useEffect(() => {
    if (!emails || emails.length === 0) return;
    const now = Date.now();
    const expiredIds = [];
    const nextTrashMap = { ...trashEmailMap };
    let mapChanged = false;

    emails.forEach(email => {
      if (isTrashEmail(email) && (isMyInboxEmail(email) || isSentEmail(email))) {
        const deletedAt = trashEmailMap[email.EmailID] || email.DeletedAt || email.CreatedAt || email.Timestamp || email.DeliveredAt || email.Date;
        if (deletedAt) {
          const elapsedDays = (now - new Date(deletedAt).getTime()) / (1000 * 60 * 60 * 24);
          if (elapsedDays >= trashRetentionDays) {
            expiredIds.push(email.EmailID);
            if (nextTrashMap[email.EmailID]) {
              delete nextTrashMap[email.EmailID];
              mapChanged = true;
            }
          }
        }
      }
    });

    if (expiredIds.length > 0) {
      console.log(`[모바일 휴지통 보존기간 만료 자동 영구삭제] ${expiredIds.length}건 영구 삭제 실행 (${trashRetentionDays}일 경과)`);
      deleteEmails(expiredIds);
      if (mapChanged) {
        saveTrashEmailMap(nextTrashMap);
      }
    }
  }, [trashRetentionDays, emails, trashEmailMap]);

  const [visibleCount, setVisibleCount] = useState(25);
  const [isSearchingDb, setIsSearchingDb] = useState(false);

  const handleSelectFolder = (folderKey) => {
    setCurrentFolder(folderKey);
    setSelectedEmail(null);
    setVisibleCount(25);
  };

  // Korean Date Formatter for email quotes (Matches Image 2 format exactly)
  const formatQuoteDate = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return String(timestamp);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const weekDays = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
    const weekDay = weekDays[d.getDay()];
    let hours = d.getHours();
    const ampm = hours >= 12 ? '오후' : '오전';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${weekDay} ${ampm} ${hours}:${minutes}:${seconds}`;
  };

  const formatQuoteBodyHtml = (email, type = 'FORWARD') => {
    if (!email) return '';
    const dateStr = formatQuoteDate(email.Timestamp || email.CreatedAt || Date.now());
    const senderDisplay = email.SenderName 
      ? `"${email.SenderName}" &lt;${email.Sender}&gt;` 
      : (email.Sender?.includes('<') ? email.Sender.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${email.Sender}" &lt;${email.Sender}&gt;`);
    const recipientDisplay = email.Recipient?.includes('<') ? email.Recipient.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${email.Recipient}" &lt;${email.Recipient}&gt;`;
    const ccDisplay = email.Cc ? (email.Cc.includes('<') ? email.Cc.replace(/</g, '&lt;').replace(/>/g, '&gt;') : email.Cc) : '';
    const subjectDisplay = email.Subject || '';
    
    let originalHtml = '';
    if (email.BodyHTML && email.BodyHTML.trim().startsWith('<')) {
      originalHtml = email.BodyHTML;
    } else if (email.BodyHTML) {
      originalHtml = convertTextToHtml(email.BodyHTML);
    } else if (email.BodyText || email.Body) {
      originalHtml = convertTextToHtml(email.BodyText || email.Body);
    } else {
      originalHtml = '<p>(본문 내용 없음)</p>';
    }

    return `<p><br></p>` +
      `<blockquote style="margin: 1.5em 0; padding-left: 14px; border-left: 2.5px solid #94a3b8; color: #475569;">` +
      `<p style="font-weight: bold; margin-bottom: 6px;">--- Original Message ---</p>` +
      `<p style="margin: 2px 0; line-height: 1.6;">` +
      `<strong>From :</strong> ${senderDisplay}<br>` +
      `<strong>To :</strong> ${recipientDisplay}<br>` +
      (ccDisplay ? `<strong>Cc :</strong> ${ccDisplay}<br>` : '') +
      `<strong>Date :</strong> ${dateStr}<br>` +
      `<strong>Subject :</strong> ${subjectDisplay}` +
      `</p>` +
      `<hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 12px 0;" />` +
      `<div style="color: #1e293b;">${originalHtml}</div>` +
      `</blockquote>`;
  };

  const convertTextToHtml = (text) => {
    if (!text) return '';
    if (text.includes('<p>') || text.includes('<div>') || text.includes('<blockquote') || text.includes('<br')) {
      return text;
    }
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped.replace(/\r\n|\r|\n/g, '<br />');
  };

  // Reset pagination on folder or search change
  useEffect(() => {
    setVisibleCount(25);
  }, [currentFolder, unreadOnly, searchTerm]);

  // Debounced Remote DynamoDB Search
  useEffect(() => {
    if (!searchTerm || !searchTerm.trim()) return;
    const timer = setTimeout(async () => {
      if (typeof fetchRemoteEmails === 'function') {
        setIsSearchingDb(true);
        try {
          await fetchRemoteEmails();
        } catch (err) {
          console.warn('Mobile remote search sync error:', err);
        } finally {
          setIsSearchingDb(false);
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Compose State
  const [isComposing, setIsComposing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [mobileSendToSelf, setMobileSendToSelf] = useState(false);
  const [mobileForwardQuotedEmail, setMobileForwardQuotedEmail] = useState(null);
  const [mobileIncludeQuotedEmail, setMobileIncludeQuotedEmail] = useState(true);
  const [mobileSendKakaoNotice, setMobileSendKakaoNotice] = useState(false);
  const [showComposePreview, setShowComposePreview] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeCc, setComposeCc] = useState('');
  const [composeBcc, setComposeBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const [showBccDropdown, setShowBccDropdown] = useState(false);

  useEffect(() => {
    if (initialCompose && initialCompose.isComposing) {
      setActiveDraftId(null);
      setMobileSendToSelf(Boolean(initialCompose.isSendToSelf));
      setMobileForwardQuotedEmail(null);
      setComposeTo(initialCompose.to || '');
      setComposeCc(initialCompose.cc || '');
      setComposeBcc(initialCompose.bcc || '');
      if (initialCompose.cc || initialCompose.bcc) {
        setShowCcBcc(true);
      }
      setComposeSubject(initialCompose.subject || '');
      setComposeBody(initialCompose.body || '');
      setAttachedFiles(initialCompose.attachments || []);
      setIsComposing(true);
      if (onClearInitialCompose) onClearInitialCompose();
    }
  }, [initialCompose]);

  const mobileFileInputRef = useRef(null);
  const mobileCameraInputRef = useRef(null);

  // Autocomplete matching helpers
  // Autocomplete matching helpers
  const userDetails = (users || []).find(u => 
    (u.UserID && currentUser?.UserID && u.UserID === currentUser.UserID) ||
    (u.Email && currentUser?.Email && u.Email.toLowerCase() === currentUser.Email.toLowerCase()) ||
    (u.Account && currentUser?.Account && u.Account.toLowerCase() === currentUser.Account.toLowerCase()) ||
    (u.Aliases && currentUser?.Email && u.Aliases.toLowerCase().includes(currentUser.Email.toLowerCase()))
  ) || currentUser;

  const rawAliasesStr = `${userDetails?.Aliases || ''},${currentUser?.Aliases || ''},${userDetails?.Email || ''},${currentUser?.Email || ''},${userDetails?.Account || ''},${currentUser?.Account || ''}`;
  const baseAliases = rawAliasesStr.split(/[,;\s]+/).map(a => a.trim().toLowerCase()).filter(Boolean);
  
  const currentEmailLower = (currentUser?.Email || userDetails?.Email || '').toLowerCase();
  const currentAccountLower = (currentUser?.Account || userDetails?.Account || '').toLowerCase();
  if (currentEmailLower.includes('chris') || currentAccountLower === 'chris') {
    baseAliases.push('ceo@clouflake.com', 'executive@clouflake.com', 'chris', 'ceo', 'executive');
  }
  if (currentEmailLower.includes('clou0315') || currentAccountLower === 'clou0315') {
    baseAliases.push('anderson@clouflake.com', 'clou0315', 'anderson');
  }
  if (currentEmailLower.includes('jykwon') || currentAccountLower === 'jykwon') {
    baseAliases.push('jykwon@clouflake.com', 'jykwon');
  }

  const userAliases = Array.from(new Set(baseAliases));
  const primaryAlias = userAliases.length > 0 ? (userAliases[0].includes('@') ? userAliases[0] : `${userAliases[0]}@clouflake.com`) : (currentUser?.Email || '');
  const defaultSenderEmail = userDetails?.DefaultSenderEmail || currentUser?.DefaultSenderEmail || primaryAlias || currentUser?.Email || '';
  const availableSenderEmails = Array.from(new Set([
    ...(defaultSenderEmail ? [defaultSenderEmail] : []),
    ...(primaryAlias ? [primaryAlias] : []),
    currentUser?.Email,
    ...userAliases.map(a => a.includes('@') ? a : `${a}@clouflake.com`)
  ].filter(Boolean)));

  const [composeFrom, setComposeFrom] = useState(defaultSenderEmail);

  const filterEmployees = (query) => {
    if (!query || !query.trim()) return (users || []).slice(0, 8);
    const lastToken = query.split(',').pop().trim().toLowerCase();
    if (!lastToken) return (users || []).slice(0, 8);
    return (users || []).filter(u => 
      (u.Name && u.Name.toLowerCase().includes(lastToken)) ||
      (u.Email && u.Email.toLowerCase().includes(lastToken)) ||
      (u.Department && u.Department.toLowerCase().includes(lastToken)) ||
      (u.JobTitle && u.JobTitle.toLowerCase().includes(lastToken)) ||
      (u.Position && u.Position.toLowerCase().includes(lastToken))
    ).slice(0, 10);
  };

  const matchingEmployeesTo = useMemo(() => filterEmployees(composeTo), [composeTo, users]);
  const matchingEmployeesCc = useMemo(() => filterEmployees(composeCc), [composeCc, users]);
  const matchingEmployeesBcc = useMemo(() => filterEmployees(composeBcc), [composeBcc, users]);

  const handleMobileRecipientKeyDown = (e, currentValue, setValue, list, setDropdown) => {
    if (e.key === 'Enter') {
      // Prevent Korean/CJK IME composition duplicate trigger
      if (e.nativeEvent && e.nativeEvent.isComposing) return;
      e.preventDefault();
      e.stopPropagation();

      if (Array.isArray(list) && list.length > 0) {
        const selected = list[0];
        const tokens = currentValue.split(',').map(t => t.trim()).filter(Boolean);
        tokens.pop();
        tokens.push(selected.Email);
        setValue(tokens.join(', ') + ', ');
        setDropdown(false);
      } else {
        setDropdown(false);
      }
    } else if (e.key === 'Escape') {
      setDropdown(false);
    }
  };

  const isSentEmail = (e) => {
    if (!e) return false;
    if (e.EmailID?.includes('-inbox')) return false;
    const sender = (e.Sender || '').toLowerCase();
    const isMeSender = userAliases.some(a => {
      const cleanA = a.toLowerCase().trim();
      if (!cleanA) return false;
      if (cleanA.includes('@')) return sender.includes(cleanA);
      return sender.includes(`${cleanA}@`) || sender.includes(`<${cleanA}@`) || sender === cleanA;
    });
    return isMeSender && e.Folder !== 'trash' && e.Folder !== 'spam';
  };

  // Robust Inbox Matcher (Matching PC Logic Exactly)
  const isMyInboxEmail = (email) => {
    if (!email) return false;

    // Sent emails sent by me (unless it's an inbox copy) should never be in inbox
    if (isSentEmail(email)) return false;

    const rec = (email.Recipient || '').toLowerCase();
    const cc = (email.Cc || '').toLowerCase();
    const bcc = (email.Bcc || '').toLowerCase();
    const owner = (email.OwnerEmail || '').toLowerCase();
    
    // 1. Direct recipient match, user alias, OwnerEmail, or broadcast (including self-mail / note to self)
    const isMeRecipient = userAliases.some(a => {
      const cleanA = a.toLowerCase().trim();
      if (!cleanA) return false;
      return rec.includes(cleanA) || cc.includes(cleanA) || bcc.includes(cleanA) || owner.includes(cleanA);
    }) || rec.includes('all@clouflake.com') || cc.includes('all@clouflake.com');

    if (isMeRecipient) return true;
    
    // 3. Representative Forwarding Group email match
    if (Array.isArray(groupMails)) {
      const isAssigned = groupMails.some(group => {
        const groupAddr = (group.GroupEmail || '').toLowerCase();
        if (!rec.includes(groupAddr) && !cc.includes(groupAddr)) return false;
        const assignees = (group.Assignees || []).map(a => a.toLowerCase().trim());
        return userAliases.some(a => assignees.includes(a));
      });
      if (isAssigned) return true;
    }
    
    return false;
  };

  const isSpamEmail = (email) => {
    if (!email) return false;
    if (email.IsSpam || email.Folder === 'spam') return true;
    const sender = (email.Sender || '').toLowerCase();
    return (blockedSenders || []).some(b => b && sender.includes(b.toLowerCase()));
  };

  // Trash filtering & auto-purge for Mobile
  const isTrashEmail = (e) => {
    if (!e) return false;
    const isTrash = Boolean(trashEmailMap[e.EmailID]) || e.Folder === 'trash' || e.IsDeleted === true;
    if (!isTrash) return false;

    const deletedAt = trashEmailMap[e.EmailID] || e.DeletedAt || e.CreatedAt || e.Timestamp || e.DeliveredAt || e.Date;
    if (deletedAt) {
      const deletedTime = new Date(deletedAt).getTime();
      const elapsedDays = (Date.now() - deletedTime) / (1000 * 60 * 60 * 24);
      if (elapsedDays >= trashRetentionDays) return false; // Expired
    }
    return true;
  };

  const isMyCleanInboxEmail = (e) => {
    return isMyInboxEmail(e) && !isSpamEmail(e) && !isTrashEmail(e);
  };

  const getEffectiveFolder = (e) => {
    if (!e) return null;
    if (isTrashEmail(e)) return null;
    if (isSentEmail(e) && !customFolderMap[e?.EmailID]) return null;
    const sender = (e?.Sender || '').toLowerCase();
    const autoRouted = senderRoutingRules[sender];
    return customFolderMap[e?.EmailID] || autoRouted || null;
  };

  // Filter Emails with strict privacy isolation & trash exclusion
  const totalInboxEmails = emails.filter(e => isMyCleanInboxEmail(e) && !isSentEmail(e) && !getEffectiveFolder(e));
  const totalSentEmails = emails.filter(e => isSentEmail(e) && !isTrashEmail(e));
  const totalStarredEmails = emails.filter(e => (isMyCleanInboxEmail(e) || isSentEmail(e)) && starredEmailIds.includes(e.EmailID) && !isTrashEmail(e));
  const totalSpamEmails = emails.filter(e => !isTrashEmail(e) && !isSentEmail(e) && isSpamEmail(e));
  const totalTrashEmails = emails.filter(e => isTrashEmail(e) && (isMyInboxEmail(e) || isSentEmail(e)));

  let folderEmails = [];
  if (currentFolder === 'inbox') folderEmails = totalInboxEmails;
  else if (currentFolder === 'sent') folderEmails = totalSentEmails;
  else if (currentFolder === 'starred') folderEmails = totalStarredEmails;
  else if (currentFolder === 'spam') folderEmails = totalSpamEmails;
  else if (currentFolder === 'draft') folderEmails = drafts;
  else if (currentFolder === 'trash') folderEmails = totalTrashEmails;
  else if (customFolders.some(f => f.id === currentFolder)) {
    folderEmails = emails.filter(e => !isSentEmail(e) && getEffectiveFolder(e) === currentFolder && !isTrashEmail(e));
  } else {
    folderEmails = totalInboxEmails;
  }

  const emailsWithAttachmentsCount = folderEmails.filter(e => Array.isArray(e.Attachments) && e.Attachments.length > 0).length;

  const filteredEmails = folderEmails.filter(e => {
    const s = searchTerm.toLowerCase().trim();
    const matchSearch = !s || 
      (e.Subject || '').toLowerCase().includes(s) || 
      (e.Sender || '').toLowerCase().includes(s) || 
      (e.Recipient || '').toLowerCase().includes(s) || 
      (e.BodyText || e.Body || '').toLowerCase().includes(s);
    const matchUnread = !unreadOnly || !e.IsRead;
    const matchAttachment = !attachmentOnly || (Array.isArray(e.Attachments) && e.Attachments.length > 0);
    return matchSearch && matchUnread && matchAttachment;
  }).sort((a, b) => new Date(b.Timestamp || b.DeliveredAt || b.CreatedAt || b.Date || 0) - new Date(a.Timestamp || a.DeliveredAt || a.CreatedAt || a.Date || 0));

  const displayedEmails = useMemo(() => {
    return filteredEmails.slice(0, visibleCount);
  }, [filteredEmails, visibleCount]);

  const handleScrollEmailList = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 120) {
      if (visibleCount < filteredEmails.length) {
        setVisibleCount(prev => Math.min(prev + 25, filteredEmails.length));
      }
    }
  };

  const unreadInboxCount = totalInboxEmails.filter(e => !e.IsRead).length;

  const handleSelectEmail = (email) => {
    if (currentFolder === 'draft' || email.Folder === 'draft' || email.DraftID || (email.id && String(email.id).startsWith('draft-'))) {
      setActiveDraftId(email.DraftID || email.id || email.EmailID);
      setComposeTo(email.to || email.Recipient || '');
      setComposeCc(email.cc || email.Cc || '');
      setComposeBcc(email.bcc || email.Bcc || '');
      setShowCcBcc(Boolean(email.cc || email.Cc || email.bcc || email.Bcc));
      setComposeSubject(email.subject || email.Subject || '');
      setComposeBody(email.body || email.BodyHTML || email.BodyText || email.Body || '');
      setAttachedFiles(Array.isArray(email.attachments || email.Attachments) ? (email.attachments || email.Attachments) : []);
      setMobileForwardQuotedEmail(null);
      setMobileSendToSelf(false);
      setIsComposing(true);
      return;
    }

    setSelectedEmail(email);
    if (!email.IsRead && currentFolder === 'inbox') {
      markEmailsRead([email.EmailID], true);
    }
  };

  const handleSendSubmit = async (e) => {
    if (e) e.preventDefault();
    if (isSending) return;

    // Block send if any attachment is still uploading to S3
    const uploadingFiles = attachedFiles.filter(f => f.isUploading);
    if (uploadingFiles.length > 0) {
      alert(`첨부파일(${uploadingFiles.map(f => f.name).join(', ')})이 아직 업로드 중입니다.\n파일 업로드가 완료된 후 다시 발송해 주세요.`);
      return;
    }

    // Check if any attachment failed to upload
    const failedFiles = attachedFiles.filter(f => f.error);
    if (failedFiles.length > 0) {
      if (!confirm(`일부 첨부파일(${failedFiles.map(f => f.name).join(', ')})의 업로드가 실패했습니다.\n해당 파일을 제외하고 메일을 발송하시겠습니까?`)) {
        return;
      }
    }

    if (!composeTo || !composeSubject) {
      alert('받는 사람과 제목을 입력해 주세요.');
      return;
    }
    setIsSending(true);
    try {
      let htmlBodyToSend = composeBody || '';
      let textBodyToSend = stripHtmlTags(composeBody || '');

      if (mobileForwardQuotedEmail && mobileIncludeQuotedEmail) {
        const dateStr = formatQuoteDate(mobileForwardQuotedEmail.Timestamp || mobileForwardQuotedEmail.CreatedAt || Date.now());
        const senderDisplay = mobileForwardQuotedEmail.SenderName 
          ? `"${mobileForwardQuotedEmail.SenderName}" &lt;${mobileForwardQuotedEmail.Sender}&gt;` 
          : (mobileForwardQuotedEmail.Sender?.includes('<') ? mobileForwardQuotedEmail.Sender.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${mobileForwardQuotedEmail.Sender}" &lt;${mobileForwardQuotedEmail.Sender}&gt;`);
        const recipientDisplay = mobileForwardQuotedEmail.Recipient?.includes('<') ? mobileForwardQuotedEmail.Recipient.replace(/</g, '&lt;').replace(/>/g, '&gt;') : `"${mobileForwardQuotedEmail.Recipient}" &lt;${mobileForwardQuotedEmail.Recipient}&gt;`;
        const ccDisplay = mobileForwardQuotedEmail.Cc ? (mobileForwardQuotedEmail.Cc.includes('<') ? mobileForwardQuotedEmail.Cc.replace(/</g, '&lt;').replace(/>/g, '&gt;') : mobileForwardQuotedEmail.Cc) : '';
        const subjectDisplay = mobileForwardQuotedEmail.Subject || '';

        const rawOriginalHtml = mobileForwardQuotedEmail.BodyHTML || convertTextToHtml(mobileForwardQuotedEmail.BodyText || mobileForwardQuotedEmail.Body || '');
        const quoteHtmlBlock = `
          <br><br>
          <blockquote style="margin: 20px 0; padding-left: 14px; border-left: 2.5px solid #94a3b8; color: #475569;">
            <p style="font-weight: bold; margin-bottom: 6px; color: #334155;">--- Original Message ---</p>
            <p style="margin: 2px 0; line-height: 1.6; font-size: 13px; color: #475569;">
              <strong>From :</strong> ${senderDisplay}<br>
              <strong>To :</strong> ${recipientDisplay}<br>
              ${ccDisplay ? `<strong>Cc :</strong> ${ccDisplay}<br>` : ''}
              <strong>Date :</strong> ${dateStr}<br>
              <strong>Subject :</strong> ${subjectDisplay}
            </p>
            <hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 12px 0;" />
            <div style="color: #1e293b;">${rawOriginalHtml}</div>
          </blockquote>
        `;

        htmlBodyToSend = (htmlBodyToSend ? htmlBodyToSend : '<p></p>') + quoteHtmlBlock;
        textBodyToSend = (textBodyToSend ? textBodyToSend : '') + `\n\n--- Original Message ---\nFrom : ${mobileForwardQuotedEmail.Sender}\nTo : ${mobileForwardQuotedEmail.Recipient}\nDate : ${dateStr}\nSubject : ${subjectDisplay}\n\n` + (mobileForwardQuotedEmail.BodyText || stripHtmlTags(mobileForwardQuotedEmail.BodyHTML || ''));
      } else {
        htmlBodyToSend = convertTextToHtml(htmlBodyToSend);
      }

      await sendEmail(composeTo, composeSubject, textBodyToSend, htmlBodyToSend, attachedFiles, composeFrom || defaultSenderEmail || currentUser?.Email, composeCc, composeBcc);
      
      // Dispatch KakaoTalk notification to recipient(s) if checked
      if (mobileSendKakaoNotice && typeof sendKakaoBizMessage === 'function') {
        const parseTokens = (str) => {
          if (!str) return [];
          return str.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean);
        };

        const rawDestinations = [
          ...parseTokens(composeTo),
          ...parseTokens(composeCc),
          ...parseTokens(composeBcc)
        ];

        const matchedRecipients = [];

        for (const raw of rawDestinations) {
          const rawClean = raw.toLowerCase();
          const angleMatch = raw.match(/<([^>]+)>/);
          const emailPart = (angleMatch ? angleMatch[1] : (raw.includes('@') ? raw : '')).trim().toLowerCase();
          const namePart = (angleMatch ? raw.replace(/<[^>]+>/, '') : (!raw.includes('@') ? raw : '')).trim().toLowerCase();
          const idPrefix = emailPart ? emailPart.split('@')[0] : rawClean;
          const phoneOnly = raw.replace(/[^0-9]/g, '');

          // 0. Check Group Mails (e.g. all@clouflake.com, dev@clouflake.com)
          const isAllGroup = emailPart === 'all@clouflake.com' || rawClean.includes('all@clouflake.com') || rawClean === 'all' || rawClean === '전체';
          if (isAllGroup) {
            (users || []).filter(u => u.EmploymentStatus !== '퇴사' && u.Phone).forEach(u => {
              matchedRecipients.push({
                Name: u.Name,
                Email: u.Email,
                Phone: u.Phone
              });
            });
            continue;
          }

          const matchedGroup = (groupMails || []).find(g => {
            const gEmail = (g.GroupEmail || g.email || '').toLowerCase().trim();
            const gName = (g.GroupName || g.name || '').toLowerCase().trim();
            return (emailPart && gEmail === emailPart) || (namePart && gName === namePart) || rawClean === gEmail || rawClean === gName;
          });

          if (matchedGroup) {
            const memberIdsOrEmails = matchedGroup.Members || matchedGroup.members || [];
            (users || []).filter(u => {
              return memberIdsOrEmails.includes(u.UserID) || 
                     memberIdsOrEmails.includes(u.Email) || 
                     memberIdsOrEmails.some(m => m.toLowerCase() === (u.Email || '').toLowerCase());
            }).forEach(u => {
              if (u.Phone && u.EmploymentStatus !== '퇴사') {
                matchedRecipients.push({
                  Name: u.Name,
                  Email: u.Email,
                  Phone: u.Phone
                });
              }
            });
            continue;
          }

          // 1. Search in Internal Directory (users)
          let foundUser = (users || []).find(u => {
            const uEmail = (u.Email || '').toLowerCase().trim();
            const uName = (u.Name || '').toLowerCase().trim();
            const uAliases = (u.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
            const uPhoneOnly = (u.Phone || '').replace(/[^0-9]/g, '');

            return (
              (emailPart && (uEmail === emailPart || uAliases.includes(emailPart))) ||
              (namePart && (uName === namePart || uName.includes(namePart))) ||
              (idPrefix && (uEmail.split('@')[0] === idPrefix || uAliases.some(a => a.split('@')[0] === idPrefix))) ||
              (rawClean && (uEmail === rawClean || uName === rawClean)) ||
              (phoneOnly.length >= 10 && uPhoneOnly === phoneOnly)
            );
          });

          if (foundUser) {
            matchedRecipients.push({
              Name: foundUser.Name,
              Email: foundUser.Email,
              Phone: foundUser.Phone
            });
            continue;
          }

          // 2. Search in Address Book External Contacts (customers)
          let foundCustomer = (customers || []).find(c => {
            const cEmail = (c.Email || '').toLowerCase().trim();
            const cName = (c.Name || '').toLowerCase().trim();
            const cPhoneOnly = (c.Phone || '').replace(/[^0-9]/g, '');

            return (
              (emailPart && cEmail === emailPart) ||
              (namePart && (cName === namePart || cName.includes(namePart))) ||
              (rawClean && (cEmail === rawClean || cName === rawClean)) ||
              (phoneOnly.length >= 10 && cPhoneOnly === phoneOnly)
            );
          });

          if (foundCustomer) {
            matchedRecipients.push({
              Name: foundCustomer.Name,
              Email: foundCustomer.Email,
              Phone: foundCustomer.Phone
            });
          } else {
            // Fallback: If sent to clou0315 or unknown internal, dispatch with email so backend resolves
            matchedRecipients.push({
              Name: namePart || idPrefix || '수신자',
              Email: emailPart || (idPrefix.includes('@') ? idPrefix : `${idPrefix}@clouflake.com`),
              Phone: phoneOnly.length >= 10 ? phoneOnly : ''
            });
          }
        }

        // Deduplicate by Phone / Email
        const uniqueRecipients = Array.from(new Map(matchedRecipients.map(u => [(u.Phone || '').replace(/[^0-9]/g, '') || u.Email, u])).values());

        const plainBody = stripHtmlTags(composeBody);
        const formattedMail = typeof formatKakaoTemplate === 'function'
          ? formatKakaoTemplate('MAIL_SUMMARY', {
              '발신자': `${currentUser?.Name || '동료'} (${composeFrom || defaultSenderEmail || currentUser?.Email || 'clouflake'})`,
              '메일제목': composeSubject || '(제목 없음)',
              '수신일시': formatMailDateTime(new Date()),
              '요약내용': plainBody ? (plainBody.length > 200 ? plainBody.slice(0, 200) + '...' : plainBody) : '새로운 업무 메일이 도착했습니다.'
            })
          : null;

        const mailTitle = formattedMail?.title || `[클라우플레이크 메일 도착 알림]`;
        const mailBody = formattedMail?.messageText || `[클라우플레이크 메일 도착 알림]\n\n발신자: ${currentUser?.Name || '동료'}\n메일제목: ${composeSubject}\n수신일시: ${formatMailDateTime(new Date())}\n\n새로운 업무 메일이 도착했습니다.\n\n* 주식회사 클로우플레이크 AX 플랫폼`;
        const mailBtnText = formattedMail?.buttonText || '메일 바로보기';
        const mailBtnUrl = formattedMail?.buttonUrl || `${window.location.origin}/#/webmail`;

        for (const recipient of uniqueRecipients) {
          try {
            await sendKakaoBizMessage({
              recipientPhone: recipient.Phone,
              recipientName: recipient.Name,
              recipientEmail: recipient.Email,
              title: mailTitle,
              messageText: mailBody,
              buttonText: mailBtnText,
              buttonUrl: mailBtnUrl,
              messageType: 'MAIL',
              apiKey: kakaoBizConfig?.apiKey,
              apiSecret: kakaoBizConfig?.apiSecret,
              senderKey: kakaoBizConfig?.senderKey,
              senderPhone: kakaoBizConfig?.senderPhone,
              provider: kakaoBizConfig?.provider || 'solapi'
            });
          } catch (kErr) {
            console.warn('[Mobile Kakao Mail Dispatch Warning]', kErr);
          }
        }
      }

      setIsComposing(false);
      setMobileSendToSelf(false);
      setComposeTo('');
      setComposeCc('');
      setComposeBcc('');
      setShowCcBcc(false);
      setComposeSubject('');
      setComposeBody('');
      setAttachedFiles([]);
      setMobileSendKakaoNotice(false);
      alert('메일이 성공적으로 발송되었습니다.');
    } catch (err) {
      console.error(err);
      alert('메일 발송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handleAttachFile = async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);
    
    for (const file of fileList) {
      let localDataUrl = null;
      try {
        localDataUrl = URL.createObjectURL(file);
      } catch (e) {
        console.warn('Failed to create object URL for attachment preview:', e);
      }

      const placeholder = {
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        s3Key: null,
        dataUrl: localDataUrl,
        isUploading: true,
        error: null
      };
      
      setAttachedFiles(prev => {
        if (prev.some(f => f.name === file.name)) return prev;
        return [...prev, placeholder];
      });
      
      try {
        const presignRes = await fetch('https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/presign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream'
          })
        });
        const presignData = await presignRes.json();
        
        if (!presignData.success || !presignData.uploadUrl) {
          throw new Error(presignData.error || 'Failed to get upload URL');
        }
        
        const { uploadUrl, s3Key } = presignData;
        
        const uploadRes = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          },
          body: file
        });
        
        if (!uploadRes.ok) {
          throw new Error(`S3 upload failed: ${uploadRes.statusText}`);
        }
        
        setAttachedFiles(prev => prev.map(f => f.name === file.name ? {
          ...f,
          s3Key,
          isUploading: false
        } : f));
        console.log(`Mobile directly uploaded ${file.name} to S3 Key: ${s3Key}`);
        
      } catch (err) {
        console.error(`Mobile S3 upload failure for ${file.name}:`, err);
        setAttachedFiles(prev => prev.map(f => f.name === file.name ? {
          ...f,
          isUploading: false,
          error: '실패'
        } : f));
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 text-slate-800">
      
      {/* 1. If an email is selected, show Fullscreen Mobile Reader */}
      {selectedEmail ? (
        <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
          {/* Top Bar */}
          <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0">
            <button
              type="button"
              onClick={() => setSelectedEmail(null)}
              className="p-2 -ml-2 text-slate-700 hover:bg-slate-100 rounded-full flex items-center gap-1 font-bold text-sm"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>목록</span>
            </button>

            <div className="flex items-center gap-1.5">
              {!isTrashEmail(selectedEmail) && (
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      promptMoveToCustomFolder(selectedEmail.EmailID, e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="bg-slate-50 border border-slate-200 text-slate-700 rounded-lg px-2 py-1 text-[11px] font-bold focus:outline-none"
                  defaultValue=""
                >
                  <option value="" disabled>📁 이동...</option>
                  <option value="inbox">📥 받은메일함</option>
                  {customFolders.map(f => (
                    <option key={f.id} value={f.id}>📁 {f.name}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={() => toggleStarEmail(selectedEmail.EmailID)}
                className="p-1.5 text-slate-400 hover:text-amber-500 rounded-full"
              >
                <Star className={`w-5 h-5 ${starredEmailIds.includes(selectedEmail.EmailID) ? 'fill-amber-400 text-amber-400' : ''}`} />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm('이 메일을 삭제하시겠습니까?')) {
                    handleMoveToTrash(selectedEmail.EmailID);
                  }
                }}
                className="p-1.5 text-slate-400 hover:text-rose-600 rounded-full"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mail Content Scroll Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-left">
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-snug">
                {selectedEmail.Subject}
              </h1>
              <span className="text-[11px] text-slate-400 mt-1 block font-mono">
                {formatMailDateTime(selectedEmail.Timestamp || selectedEmail.DeliveredAt || selectedEmail.CreatedAt || selectedEmail.Date)}
              </span>
            </div>

            {/* Sender & Recipient Card */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">
                  {selectedEmail.Sender?.slice(0, 1).toUpperCase()}
                </div>
                <div className="overflow-hidden">
                  <div className="font-bold text-xs text-slate-900 truncate">{selectedEmail.Sender}</div>
                  <div className="text-[11px] text-slate-500 truncate">받는사람: {selectedEmail.Recipient}</div>
                </div>
              </div>
            </div>

            {/* Attachments (if any) */}
            {selectedEmail.Attachments && selectedEmail.Attachments.length > 0 && (
              <div className="space-y-1.5 p-3 bg-indigo-50/50 border border-indigo-100 rounded-2xl">
                <span className="text-[11px] font-bold text-indigo-900 flex items-center gap-1">
                  <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
                  <span>첨부파일 ({selectedEmail.Attachments.length}개)</span>
                </span>
                <div className="space-y-1">
                  {selectedEmail.Attachments.map((att, idx) => {
                    const filename = typeof att === 'string' ? att.split('/').pop() : (att.name || att.FileName || '첨부문서');
                    return (
                      <div key={idx} className="flex items-center justify-between p-2.5 bg-white rounded-xl border border-slate-200 text-xs">
                        <span className="truncate font-semibold text-slate-800 max-w-[180px]">{filename}</span>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              const cleanEmailId = selectedEmail?.EmailID ? selectedEmail.EmailID.replace(/-inbox.*/, '') : '';
                              let rawKey = typeof att === 'string' 
                                ? (att.includes('/') ? att : `attachments/${cleanEmailId}/${att}`)
                                : (att.s3Key || att.key || (cleanEmailId ? `attachments/${cleanEmailId}/${att.name || att.FileName}` : null));
                              let s3Key = rawKey;
                              if (cleanEmailId && s3Key && (s3Key.includes('temp-') || !s3Key.startsWith(`attachments/${cleanEmailId}/`))) {
                                const fn = filename || (typeof att === 'string' ? att.split('/').pop() : (att.name || att.FileName || 'file'));
                                s3Key = `attachments/${cleanEmailId}/${fn}`;
                              }
                              const dataUrl = typeof att === 'object' ? (att.dataUrl || att.url) : (typeof att === 'string' && (att.startsWith('data:') || att.startsWith('http') || att.startsWith('blob:')) ? att : null);
                              setPreviewAttachment({
                                name: filename,
                                s3Key: s3Key,
                                dataUrl: dataUrl,
                                size: att.size || 0,
                                type: att.type || '',
                                email: selectedEmail
                              });
                            }}
                            className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-lg text-[11px] flex items-center gap-1 active:scale-95 transition-transform"
                          >
                            <Eye className="w-3 h-3" />
                            <span>미리보기</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Mail Body */}
            <div className="pt-2 text-sm text-slate-800 leading-relaxed break-words font-sans border-t border-slate-100 overflow-x-auto">
              {selectedEmail.BodyHTML && selectedEmail.BodyHTML.trim().startsWith('<') ? (
                <div 
                  className="mail-html-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.BodyHTML) }} 
                />
              ) : (
                <div className="whitespace-pre-wrap font-sans text-slate-800 leading-relaxed">
                  {selectedEmail.BodyText || selectedEmail.BodyHTML || selectedEmail.Body || '(본문 내용이 없습니다.)'}
                </div>
              )}
            </div>
          </div>

          {/* Quick Action Bottom Bar (Reply, Reply All, Forward) */}
          <div className="p-2.5 border-t border-slate-200 bg-white flex items-center justify-around gap-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const quoteHtml = formatQuoteBodyHtml(selectedEmail);
                setActiveDraftId(null);
                setMobileSendToSelf(false);
                setMobileForwardQuotedEmail(selectedEmail);
                setMobileIncludeQuotedEmail(true);
                setComposeTo(selectedEmail.Sender || '');
                setComposeCc('');
                setComposeBcc('');
                setShowCcBcc(false);
                setComposeSubject(selectedEmail.Subject?.startsWith('Re:') ? selectedEmail.Subject : `Re: ${selectedEmail.Subject || ''}`);
                setComposeBody(`<p></p><br>${quoteHtml}`);
                setAttachedFiles([]);
                setIsComposing(true);
              }}
              className="flex-1 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl text-xs flex items-center justify-center gap-1 shadow-2xs cursor-pointer transition-all active:scale-98"
            >
              <CornerUpLeft className="w-3.5 h-3.5" />
              <span>답장</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const targetEmail = selectedEmail.Sender || '';
                const allCc = [selectedEmail.Recipient, ...(selectedEmail.Cc ? selectedEmail.Cc.split(',') : [])]
                  .map(s => s.trim())
                  .filter(s => s && s.toLowerCase() !== currentUser?.Email?.toLowerCase() && s.toLowerCase() !== targetEmail.toLowerCase());
                const quoteHtml = formatQuoteBodyHtml(selectedEmail);
                setActiveDraftId(null);
                setMobileSendToSelf(false);
                setMobileForwardQuotedEmail(selectedEmail);
                setMobileIncludeQuotedEmail(true);
                setComposeTo(targetEmail);
                setComposeCc(allCc.join(', '));
                setComposeBcc('');
                setShowCcBcc(allCc.length > 0);
                setComposeSubject(selectedEmail.Subject?.startsWith('Re:') ? selectedEmail.Subject : `Re: ${selectedEmail.Subject || ''}`);
                setComposeBody(`<p></p><br>${quoteHtml}`);
                setAttachedFiles([]);
                setIsComposing(true);
              }}
              className="flex-1 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-xl text-xs flex items-center justify-center gap-1 shadow-2xs cursor-pointer transition-all active:scale-98"
            >
              <ReplyAll className="w-3.5 h-3.5" />
              <span>전체답장</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const quoteHtml = formatQuoteBodyHtml(selectedEmail);
                setActiveDraftId(null);
                setMobileSendToSelf(false);
                setMobileForwardQuotedEmail(selectedEmail);
                setMobileIncludeQuotedEmail(true);
                setComposeTo('');
                setComposeCc('');
                setComposeBcc('');
                setShowCcBcc(false);
                setComposeSubject(selectedEmail.Subject?.startsWith('Fwd:') || selectedEmail.Subject?.startsWith('FW:') ? selectedEmail.Subject : `Fwd: ${selectedEmail.Subject || ''}`);
                setComposeBody(`<p></p><br>${quoteHtml}`);
                setAttachedFiles(selectedEmail.Attachments ? selectedEmail.Attachments.map(att => {
                  if (typeof att === 'string') {
                    return { name: att.split('/').pop(), size: 256000, type: 'application/octet-stream', s3Key: att };
                  } else {
                    return {
                      name: att.name || 'attachment',
                      size: att.size || 0,
                      type: att.type || '',
                      s3Key: att.s3Key || att.key || null
                    };
                  }
                }) : []);
                setIsComposing(true);
              }}
              className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center justify-center gap-1 shadow-2xs cursor-pointer transition-all active:scale-98"
            >
              <CornerUpRight className="w-3.5 h-3.5" />
              <span>전달</span>
            </button>
          </div>
        </div>
      ) : (
        /* 2. Main Mobile Mailbox List */
        <div className="flex-1 flex flex-col overflow-hidden pb-16">
          
          {/* Mobile Resignation D-Day Guidance Banner */}
          {resignationDDayInfo && (
            <div className="bg-gradient-to-r from-rose-900 to-rose-950 text-white px-3.5 py-2.5 border-b border-rose-800/60 flex items-center justify-between gap-2 text-left flex-shrink-0">
              <div className="flex items-center gap-2 overflow-hidden">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black flex-shrink-0 ${resignationDDayInfo.badgeClass}`}>
                  {resignationDDayInfo.dDayLabel}
                </span>
                <div className="truncate">
                  <span className="text-xs font-bold text-rose-100 block truncate">
                    사직원 최종 승인 ({resignationDDayInfo.resignationDate} 퇴사)
                  </span>
                  <span className="text-[10px] text-rose-200/80 block truncate">
                    {resignationDDayInfo.subMessage}
                  </span>
                </div>
              </div>
              <a
                href="#/contract"
                className="px-2.5 py-1 bg-rose-600 active:bg-rose-700 text-white text-[11px] font-bold rounded-lg whitespace-nowrap shadow-2xs flex-shrink-0"
              >
                문서보기
              </a>
            </div>
          )}

          {/* Mobile Folder Tabs Strip */}
          <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-shrink-0">
            {[
              { id: 'inbox', label: '📥 받은메일', badge: unreadInboxCount },
              { id: 'sent', label: '📤 보낸메일' },
              { id: 'starred', label: '⭐ 중요메일', badge: totalStarredEmails.length },
              { id: 'draft', label: '📝 임시보관' },
              { id: 'spam', label: '🚫 스팸메일', badge: totalSpamEmails.length },
              { id: 'trash', label: '🗑️ 휴지통', badge: totalTrashEmails.length },
              ...customFolders.map(f => {
                const count = emails.filter(e => getEffectiveFolder(e) === f.id && !isTrashEmail(e)).length;
                return { id: f.id, label: `📁 ${f.name}`, badge: count, isCustom: true, name: f.name };
              })
            ].map(f => (
              <div key={f.id} className="relative flex items-center flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleSelectFolder(f.id)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                    currentFolder === f.id
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <span>{f.label}</span>
                  {f.badge > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${currentFolder === f.id ? 'bg-white text-indigo-700 font-extrabold' : 'bg-indigo-600 text-white'}`}>
                      {f.badge}
                    </span>
                  )}
                </button>
                {f.isCustom && currentFolder === f.id && (
                  <div className="flex items-center gap-0.5 ml-1">
                    <button
                      type="button"
                      onClick={() => handleRenameCustomFolder(f.id, f.name)}
                      className="p-1 bg-amber-50 text-amber-700 rounded-full text-[10px] font-bold border border-amber-200"
                      title="메일함 이름 수정"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCustomFolder(f.id, f.name)}
                      className="p-1 bg-rose-50 text-rose-600 rounded-full text-[10px] font-bold border border-rose-200"
                      title="메일함 삭제"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddCustomFolder}
              className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-all flex items-center gap-1 cursor-pointer flex-shrink-0"
              title="새 메일함 추가"
            >
              <span>+ 메일함 추가</span>
            </button>
          </div>

          {/* Search & Filter Bar */}
          <div className="p-3 bg-white border-b border-slate-200 space-y-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="메일 검색..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-600 font-medium"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-700 p-0.5"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {currentFolder === 'inbox' && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setUnreadOnly(!unreadOnly)}
                    className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors ${
                      unreadOnly ? 'bg-indigo-100 text-indigo-700 font-extrabold' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {unreadOnly ? '✓ 안읽음' : '안읽음'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAttachmentOnly(!attachmentOnly)}
                    className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-colors flex items-center gap-1 ${
                      attachmentOnly ? 'bg-[#5C5FEF] text-white font-extrabold' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <Paperclip className="w-3 h-3" />
                    <span>{attachmentOnly ? `✓ 첨부 (${emailsWithAttachmentsCount})` : `첨부 (${emailsWithAttachmentsCount})`}</span>
                  </button>
                </div>
                <span className="text-[11px] text-slate-400 font-medium">총 {filteredEmails.length}개</span>
              </div>
            )}
          </div>

          {/* Email Cards Stream with Scroll Pagination */}
          <div 
            onScroll={handleScrollEmailList}
            className="flex-1 overflow-y-auto divide-y divide-slate-100"
          >
            {isSearchingDb && (
              <div className="p-2 bg-indigo-50 text-indigo-700 text-xs font-bold text-center flex items-center justify-center gap-1.5 animate-pulse">
                <span>🔍 DynamoDB 원격 실시간 검색 중...</span>
              </div>
            )}

            {filteredEmails.length === 0 ? (
              <div className="text-center py-20 text-slate-400 space-y-2">
                <Mail className="w-10 h-10 mx-auto text-slate-300 stroke-1" />
                <p className="text-xs font-semibold">
                  {isSearchingDb ? '검색 중입니다...' : '표시할 메일이 없습니다.'}
                </p>
              </div>
            ) : (
              <>
                {displayedEmails.map((email, idx) => (
                  <div
                    key={email.EmailID || email.id || `mail-${idx}`}
                    onClick={() => handleSelectEmail(email)}
                    className={`p-3.5 transition-colors active:bg-slate-100 cursor-pointer flex items-start gap-3 text-left ${
                      !email.IsRead && currentFolder === 'inbox' ? 'bg-indigo-50/40' : 'bg-white'
                    }`}
                  >
                    {/* Left Avatar & Unread Indicator */}
                    <div className="relative flex-shrink-0 mt-0.5">
                      <div className="w-9 h-9 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-xs">
                        {currentFolder === 'draft' ? '📝' : ((email.SenderName || email.Sender || 'M').slice(0, 1).toUpperCase())}
                      </div>
                      {!email.IsRead && currentFolder === 'inbox' && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-indigo-600 rounded-full ring-2 ring-white" />
                      )}
                    </div>

                    {/* Mail Info */}
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex justify-between items-center gap-1">
                        <span className={`text-xs truncate ${!email.IsRead && currentFolder === 'inbox' ? 'font-black text-slate-900' : 'font-bold text-slate-700'}`}>
                          {currentFolder === 'draft' ? (email.to || email.Recipient ? `To: ${email.to || email.Recipient}` : '(수신자 지정 필요)') : (currentFolder === 'sent' ? `To: ${email.Recipient}` : (email.SenderName ? `${email.SenderName}` : email.Sender))}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono flex-shrink-0">
                          {formatMailDateTime(email.Timestamp || email.DeliveredAt || email.CreatedAt || email.updatedAt || email.Date)}
                        </span>
                      </div>

                      <div className={`text-xs truncate ${!email.IsRead && currentFolder === 'inbox' ? 'font-bold text-slate-900' : 'text-slate-800'}`}>
                        {email.Subject || email.subject || '(제목 없음)'}
                      </div>

                      <div className="text-[11px] text-slate-400 truncate">
                        {stripHtmlTags(email.BodyText || email.BodyHTML || email.Body || email.body) || '(본문 없음)'}
                      </div>
                    </div>

                    {/* Star Toggle */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStarEmail(email.EmailID || email.id);
                      }}
                      className="p-1 -mr-1 text-slate-300 hover:text-amber-400 self-center"
                    >
                      <Star className={`w-4 h-4 ${starredEmailIds.includes(email.EmailID || email.id) ? 'fill-amber-400 text-amber-400' : ''}`} />
                    </button>
                  </div>
                ))}

                {/* Scroll Pagination Load Indicator */}
                {visibleCount < filteredEmails.length && (
                  <div className="p-3.5 bg-slate-50 text-center text-xs text-slate-500 font-bold flex items-center justify-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-indigo-600 animate-ping" />
                    <span>아래로 스크롤하여 더 불러오기 ({displayedEmails.length} / {filteredEmails.length})</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Floating Action Button for Compose */}
          <button
            type="button"
            onClick={() => {
              setMobileSendToSelf(false);
              setMobileForwardQuotedEmail(null);
              setMobileIncludeQuotedEmail(true);
              setComposeTo('');
              setComposeSubject('');
              setComposeBody('');
              setAttachedFiles([]);
              setIsComposing(true);
            }}
            className="fixed right-5 bottom-20 z-40 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-xl flex items-center justify-center cursor-pointer transition-transform active:scale-95"
            title="메일 쓰기"
          >
            <Plus className="w-6 h-6 stroke-3" />
          </button>
        </div>
      )}

      {/* 3. Fullscreen Mobile Compose Modal */}
      {isComposing && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in slide-in-from-bottom duration-200">
          {/* Header */}
          <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between bg-white flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                if (!composeTo && !composeSubject && !composeBody) {
                  setIsComposing(false);
                } else if (confirm('작성 중인 메일을 닫으시겠습니까?')) {
                  setIsComposing(false);
                }
              }}
              className="p-2 -ml-2 text-slate-500 hover:text-slate-800 rounded-full cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <span className="font-bold text-sm text-slate-900 truncate px-2">
              {activeDraftId ? '임시보관 메일 수정' : (mobileForwardQuotedEmail ? (composeSubject.startsWith('Fwd:') || composeSubject.startsWith('FW:') ? '메일 전달' : '답장 쓰기') : (mobileSendToSelf ? '내게 쓰기' : '새 메일 작성'))}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  if (!composeSubject && !composeBody && !composeTo) {
                    alert('저장할 내용이 없습니다.');
                    return;
                  }
                  saveDraft({
                    id: activeDraftId || `draft-${Date.now()}`,
                    to: composeTo,
                    cc: composeCc,
                    bcc: composeBcc,
                    subject: composeSubject || '(제목 없음)',
                    body: composeBody,
                    attachments: attachedFiles
                  });
                  alert('임시보관함에 저장되었습니다.');
                  setIsComposing(false);
                }}
                className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 shadow-2xs cursor-pointer"
              >
                <span>저장</span>
              </button>
              <button
                type="button"
                disabled={isSending || attachedFiles.some(f => f.isUploading)}
                onClick={handleSendSubmit}
                className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1 shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : attachedFiles.some(f => f.isUploading) ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                <span>
                  {isSending 
                    ? '발송 중...' 
                    : attachedFiles.some(f => f.isUploading) 
                      ? '업로드 중...' 
                      : '보내기'}
                </span>
              </button>
            </div>
          </div>

          {/* Form Fields */}
          <form onSubmit={handleSendSubmit} className="flex-1 flex flex-col p-4 space-y-3 overflow-y-auto text-left">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">보내는 사람 (From)</label>
              {availableSenderEmails.length > 1 ? (
                <select
                  value={composeFrom}
                  onChange={e => setComposeFrom(e.target.value)}
                  className="w-full px-3 py-2 bg-indigo-50/50 border border-indigo-200 rounded-xl text-xs font-bold text-indigo-900 focus:outline-none focus:border-indigo-600"
                >
                  {availableSenderEmails.map((email, idx) => (
                    <option key={idx} value={email}>
                      {email} {email === defaultSenderEmail ? '(기본 발신)' : email === primaryAlias ? '(대표 별칭)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-slate-100 rounded-xl text-xs font-semibold text-slate-700 truncate">
                  {composeFrom || currentUser.Email}
                </div>
              )}
            </div>

            {/* Recipient Autocomplete (To) */}
            <div className="space-y-1 relative">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] font-bold text-slate-400 uppercase">받는 사람 *</label>
                  <label className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 active:bg-indigo-100 border border-indigo-200 rounded text-[10px] font-bold text-indigo-700 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mobileSendToSelf}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setMobileSendToSelf(checked);
                        if (checked) {
                          setComposeTo(primaryAlias || currentUser?.Email || '');
                          setShowToDropdown(false);
                        } else {
                          if (composeTo === (primaryAlias || currentUser?.Email || '')) {
                            setComposeTo('');
                          }
                        }
                      }}
                      className="w-3 h-3 rounded text-indigo-600 focus:ring-indigo-500 border-indigo-300"
                    />
                    <span>내게 쓰기</span>
                  </label>
                </div>
                <span className="text-[10px] text-slate-400 font-medium">
                  {mobileSendToSelf ? '내 대표 이메일로 지정됨' : '임직원 검색 가능'}
                </span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder={mobileSendToSelf ? '내 대표 이메일로 발송됩니다' : '직원 이름(예: 홍길동) 또는 이메일 후 Enter...'}
                  value={composeTo}
                  readOnly={mobileSendToSelf}
                  onFocus={() => {
                    if (!mobileSendToSelf) setShowToDropdown(true);
                  }}
                  onChange={e => {
                    if (!mobileSendToSelf) {
                      setComposeTo(e.target.value);
                      setShowToDropdown(true);
                    }
                  }}
                  onKeyDown={e => {
                    if (!mobileSendToSelf) {
                      handleMobileRecipientKeyDown(e, composeTo, setComposeTo, matchingEmployeesTo, setShowToDropdown);
                    }
                  }}
                  className={`w-full pl-3 pr-8 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    mobileSendToSelf 
                      ? 'bg-indigo-50/60 border border-indigo-200 text-indigo-950 select-none cursor-default' 
                      : 'bg-white border border-slate-300 text-slate-900 focus:outline-none focus:border-indigo-600'
                  }`}
                  required
                />
                {composeTo && !mobileSendToSelf && (
                  <button
                    type="button"
                    onClick={() => setComposeTo('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Autocomplete Dropdown for To */}
              {!mobileSendToSelf && showToDropdown && matchingEmployeesTo.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto divide-y divide-slate-100 text-left">
                  <div className="px-3 py-1.5 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between border-b border-slate-100">
                    <span>🏢 사내 임직원 검색 ({matchingEmployeesTo.length}명)</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowToDropdown(false);
                      }}
                      className="text-slate-400 hover:text-slate-700 font-bold px-1 rounded cursor-pointer"
                    >
                      ✕ 닫기
                    </button>
                  </div>
                  {matchingEmployeesTo.map(emp => (
                    <div
                      key={emp.UserID}
                      onClick={() => {
                        const tokens = composeTo.split(',').map(t => t.trim()).filter(Boolean);
                        tokens.pop();
                        tokens.push(emp.Email);
                        setComposeTo(tokens.join(', ') + ', ');
                        setShowToDropdown(false);
                      }}
                      className="px-3 py-2 hover:bg-indigo-50/70 active:bg-indigo-100 cursor-pointer flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-[10px] flex-shrink-0">
                          {emp.Name?.charAt(0)}
                        </div>
                        <div className="truncate">
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-xs text-slate-900">{emp.Name}</span>
                            <span className="text-[10px] px-1 py-0.2 rounded bg-slate-100 text-slate-600">{emp.Department}</span>
                          </div>
                          <span className="text-[10px] text-slate-500 font-mono block">{emp.Email}</span>
                        </div>
                      </div>
                      <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded flex-shrink-0">
                        +추가
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CC & BCC Toggle */}
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => setShowCcBcc(prev => !prev)}
                className="text-[11px] font-bold text-indigo-600 flex items-center gap-1 cursor-pointer"
              >
                <span>{showCcBcc ? '▲ 참조/숨은참조 닫기' : '+ 참조(CC) / 숨은참조(BCC)'}</span>
              </button>
              {showCcBcc && (
                <div className="space-y-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl">
                  {/* CC Field with Autocomplete */}
                  <div className="space-y-0.5 relative">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">참조 (CC)</label>
                    <input
                      type="text"
                      placeholder="이름 또는 이메일 검색 (Enter 입력)..."
                      value={composeCc}
                      onFocus={() => setShowCcDropdown(true)}
                      onChange={e => {
                        setComposeCc(e.target.value);
                        setShowCcDropdown(true);
                      }}
                      onKeyDown={e => handleMobileRecipientKeyDown(e, composeCc, setComposeCc, matchingEmployeesCc, setShowCcDropdown)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                    />
                    {showCcDropdown && matchingEmployeesCc.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-36 overflow-y-auto divide-y divide-slate-100 text-left">
                        <div className="px-2.5 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between border-b border-slate-100">
                          <span>참조 검색 결과</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCcDropdown(false);
                            }}
                            className="text-slate-400 hover:text-slate-700 font-bold px-1 rounded cursor-pointer"
                          >
                            ✕ 닫기
                          </button>
                        </div>
                        {matchingEmployeesCc.map(emp => (
                          <div
                            key={emp.UserID}
                            onClick={() => {
                              const tokens = composeCc.split(',').map(t => t.trim()).filter(Boolean);
                              tokens.pop();
                              tokens.push(emp.Email);
                              setComposeCc(tokens.join(', ') + ', ');
                              setShowCcDropdown(false);
                            }}
                            className="px-2.5 py-1.5 hover:bg-indigo-50 cursor-pointer flex items-center justify-between text-xs"
                          >
                            <span className="font-bold">{emp.Name} <span className="text-[10px] text-slate-400 font-normal">({emp.Email})</span></span>
                            <span className="text-[10px] text-indigo-600 font-bold">+추가</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* BCC Field with Autocomplete */}
                  <div className="space-y-0.5 relative">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">숨은 참조 (BCC)</label>
                    <input
                      type="text"
                      placeholder="이름 또는 이메일 검색 (Enter 입력)..."
                      value={composeBcc}
                      onFocus={() => setShowBccDropdown(true)}
                      onChange={e => {
                        setComposeBcc(e.target.value);
                        setShowBccDropdown(true);
                      }}
                      onKeyDown={e => handleMobileRecipientKeyDown(e, composeBcc, setComposeBcc, matchingEmployeesBcc, setShowBccDropdown)}
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:outline-none focus:border-indigo-600"
                    />
                    {showBccDropdown && matchingEmployeesBcc.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-36 overflow-y-auto divide-y divide-slate-100 text-left">
                        <div className="px-2.5 py-1 bg-slate-50 text-[10px] font-bold text-slate-500 uppercase flex items-center justify-between border-b border-slate-100">
                          <span>숨은 참조 검색 결과</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBccDropdown(false);
                            }}
                            className="text-slate-400 hover:text-slate-700 font-bold px-1 rounded cursor-pointer"
                          >
                            ✕ 닫기
                          </button>
                        </div>
                        {matchingEmployeesBcc.map(emp => (
                          <div
                            key={emp.UserID}
                            onClick={() => {
                              const tokens = composeBcc.split(',').map(t => t.trim()).filter(Boolean);
                              tokens.pop();
                              tokens.push(emp.Email);
                              setComposeBcc(tokens.join(', ') + ', ');
                              setShowBccDropdown(false);
                            }}
                            className="px-2.5 py-1.5 hover:bg-indigo-50 cursor-pointer flex items-center justify-between text-xs"
                          >
                            <span className="font-bold">{emp.Name} <span className="text-[10px] text-slate-400 font-normal">({emp.Email})</span></span>
                            <span className="text-[10px] text-indigo-600 font-bold">+추가</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">제목 *</label>
              <input
                type="text"
                placeholder="제목을 입력하세요"
                value={composeSubject}
                onChange={e => setComposeSubject(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-bold"
                required
              />
            </div>

            {/* Attachments Section */}
            <div>
              <input
                type="file"
                ref={mobileFileInputRef}
                onChange={e => handleAttachFile(e.target.files)}
                multiple
                className="hidden"
              />
              <input
                type="file"
                ref={mobileCameraInputRef}
                onChange={e => handleAttachFile(e.target.files)}
                accept="image/*"
                capture="environment"
                className="hidden"
              />

              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => mobileFileInputRef.current?.click()}
                  className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1"
                >
                  <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
                  <span>파일 첨부</span>
                </button>
                <button
                  type="button"
                  onClick={() => mobileCameraInputRef.current?.click()}
                  className="flex-1 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold flex items-center justify-center gap-1"
                >
                  <Camera className="w-3.5 h-3.5 text-indigo-600" />
                  <span>사진 촬영</span>
                </button>
              </div>

              {attachedFiles.length > 0 && (
                <div className="space-y-1 bg-slate-50 p-2 rounded-xl border border-slate-200">
                  {attachedFiles.map((f, i) => (
                    <div key={i} className="flex justify-between items-center text-xs p-1">
                      <div className="flex items-center gap-1.5 truncate">
                        <span className="truncate max-w-[200px] text-slate-700 font-semibold">{f.name}</span>
                        {f.isUploading && (
                          <span className="text-[10px] text-indigo-600 animate-pulse font-bold">업로드 중...</span>
                        )}
                        {f.error && (
                          <span className="text-[10px] text-rose-600 font-bold">{f.error}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setAttachedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-rose-500 font-bold px-1"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* KakaoTalk Notification Option */}
            <div 
              onClick={() => setMobileSendKakaoNotice(prev => !prev)}
              className="bg-amber-50 border border-amber-200 p-2.5 rounded-xl flex items-center justify-between cursor-pointer hover:bg-amber-100/60 transition-all select-none"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-amber-400 text-slate-950 flex items-center justify-center font-bold text-xs flex-shrink-0">
                  💬
                </div>
                <div>
                  <span className="text-[11px] font-bold text-slate-900 block">수신자 카톡 알림 동시 전송</span>
                  <span className="text-[9px] text-slate-500 font-medium">사내 임직원에게 카톡 메일 알림</span>
                </div>
              </div>
              <div className="relative inline-flex items-center flex-shrink-0">
                <input
                  type="checkbox"
                  checked={mobileSendKakaoNotice}
                  onChange={e => setMobileSendKakaoNotice(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-9 h-5 rounded-full transition-all relative ${mobileSendKakaoNotice ? 'bg-amber-500' : 'bg-slate-300'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-[2px] transition-all shadow-xs ${mobileSendKakaoNotice ? 'right-[2px]' : 'left-[2px]'}`} />
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 flex flex-col space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase">본문 내용 (CKEditor 최신 서식 & 이미지 지원)</label>
              <CKMailEditor
                content={composeBody}
                onChange={(html) => setComposeBody(html)}
                placeholder={mobileForwardQuotedEmail ? "전달/답장 추가 메시지를 입력하세요..." : "내용을 입력하세요... (이미지 붙여넣기, 표, 서식 지원)"}
                minHeight="220px"
              />
            </div>

            {/* Mobile Lossless Raw Quoted Email Card */}
            {mobileForwardQuotedEmail && (
              <div className="border border-indigo-200 bg-indigo-50/50 rounded-2xl p-3 text-left space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-black text-indigo-950">📎 원본 메일 (무손실 보존)</span>
                    <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1.5 py-0.2 rounded-full font-bold">100% 원본</span>
                  </div>
                  <label className="flex items-center gap-1 text-[11px] font-bold text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={mobileIncludeQuotedEmail}
                      onChange={e => setMobileIncludeQuotedEmail(e.target.checked)}
                      className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                    />
                    <span>발송 포함</span>
                  </label>
                </div>

                {mobileIncludeQuotedEmail && (
                  <div className="bg-white border border-slate-200 rounded-xl p-3 max-h-[220px] overflow-y-auto shadow-2xs text-xs">
                    <div className="text-[11px] text-slate-600 space-y-0.5 pb-2 border-b border-slate-100">
                      <div><strong className="text-slate-900">From:</strong> {mobileForwardQuotedEmail.SenderName || mobileForwardQuotedEmail.Sender}</div>
                      <div><strong className="text-slate-900">Subject:</strong> {mobileForwardQuotedEmail.Subject}</div>
                    </div>
                    <div 
                      className="mail-html-body pt-2 text-xs"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(mobileForwardQuotedEmail.BodyHTML || convertTextToHtml(mobileForwardQuotedEmail.BodyText || mobileForwardQuotedEmail.Body || '')) }}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {/* Move Mail Options Modal for Mobile */}
      {moveOptionModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-sm w-full p-5 text-left space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-bold flex-shrink-0">
                📁
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">메일함 이동 옵션</h3>
                <p className="text-xs text-slate-500">
                  <strong className="text-indigo-600 font-bold">'{moveOptionModal.targetFolderName}'</strong>(으)로 이동합니다.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-500">발신자</span>
                <span className="font-semibold text-slate-900 truncate max-w-[180px]">{moveOptionModal.sender}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-500">동일 발신자 메일</span>
                <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100 text-[11px]">
                  총 {moveOptionModal.sameSenderCount}개
                </span>
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => executeMoveToCustomFolder(moveOptionModal.emailIds, moveOptionModal.targetFolderId, false)}
                className="w-full py-2.5 px-3 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 font-bold rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all shadow-2xs"
              >
                <span>이 메일만 이동</span>
                <span className="text-[10px] text-slate-400 font-normal">선택한 메일만 ❯</span>
              </button>

              <button
                type="button"
                onClick={() => executeMoveToCustomFolder(moveOptionModal.emailIds, moveOptionModal.targetFolderId, true)}
                className="w-full py-2.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all shadow-2xs"
              >
                <span>동일 발신자 전체 ({moveOptionModal.sameSenderCount}개)</span>
                <span className="text-[10px] text-indigo-100 font-normal">모두 함께 ❯</span>
              </button>

              <button
                type="button"
                onClick={() => setMoveOptionModal(prev => ({ ...prev, isOpen: false }))}
                className="w-full py-1.5 text-slate-500 hover:text-slate-700 font-bold text-xs cursor-pointer text-center"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewAttachment && (
        <DocumentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
};

export default MobileMailTab;
